import React, { useState, useCallback } from 'react';
import { FilterState } from '../hooks/useUserFilters';
import { useDebounce } from '../hooks/useDebounce';
import './UserFilters.css';
import { BUSINESS_DAY_HINT } from '../utils/businessDay';

interface UserFiltersProps {
  filters: FilterState;
  updateFilter: (field: keyof FilterState, value: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  uniqueRoles: string[];
  availableSupervisors: Array<{ id: string; name: string; role: string }>;
  filterStats: {
    total: number;
    filtered: number;
    hidden: number;
  };
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  updateFilter,
  clearFilters,
  hasActiveFilters,
  uniqueRoles,
  availableSupervisors,
  filterStats
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Debounce the filter updates for better performance
  const debouncedFilters = useDebounce(localFilters, 300);

  // Update parent filters when debounced values change
  React.useEffect(() => {
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (filters[key as keyof FilterState] !== value) {
        updateFilter(key as keyof FilterState, value);
      }
    });
  }, [debouncedFilters, filters, updateFilter]);

  // Handle local filter changes
  const handleFilterChange = useCallback((field: keyof FilterState, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setLocalFilters({
      username: '',
      fullName: '',
      email: '',
      role: '',
      supervisor: '',
      status: 'all',
      lastLoginFrom: '',
      lastLoginTo: '',
      globalSearch: ''
    });
    clearFilters();
  }, [clearFilters]);

  return (
    <div className="user-filters">
      <div className="filters-header">
        <div className="filters-info">
          <h4>Filter Users</h4>
          <div className="filter-stats">
            <span className="stats-text">
              Showing <strong>{filterStats.filtered}</strong> of <strong>{filterStats.total}</strong> users
              {filterStats.hidden > 0 && (
                <span className="hidden-count"> ({filterStats.hidden} filtered out)</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="filters-controls">
          {hasActiveFilters && (
            <button 
              onClick={handleClearFilters}
              className="clear-filters-btn"
              title="Clear all filters"
            >
              Clear Filters ({Object.values(filters).filter(v => v && v !== 'all').length})
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`toggle-filters-btn ${isExpanded ? 'expanded' : ''}`}
            title={isExpanded ? 'Hide filters' : 'Show filters'}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="filter-icon"
            >
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
            </svg>
            Filters
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="filters-content">
          {/* Global Search */}
          <div className="filter-row">
            <div className="filter-group global-search">
              <label htmlFor="globalSearch">🔍 Global Search</label>
              <input
                id="globalSearch"
                type="text"
                value={localFilters.globalSearch}
                onChange={(e) => handleFilterChange('globalSearch', e.target.value)}
                placeholder="Search across all fields..."
                className="filter-input global-search-input"
              />
              <small className="filter-hint">
                Search username, name, email, role, supervisor, and status
              </small>
            </div>
          </div>

          {/* Individual Field Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="usernameFilter">Username</label>
              <input
                id="usernameFilter"
                type="text"
                value={localFilters.username}
                onChange={(e) => handleFilterChange('username', e.target.value)}
                placeholder="Filter by username..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="fullNameFilter">Full Name</label>
              <input
                id="fullNameFilter"
                type="text"
                value={localFilters.fullName}
                onChange={(e) => handleFilterChange('fullName', e.target.value)}
                placeholder="Filter by full name..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="emailFilter">Email</label>
              <input
                id="emailFilter"
                type="text"
                value={localFilters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                placeholder="Filter by email..."
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="roleFilter">Role</label>
              <select
                id="roleFilter"
                value={localFilters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="filter-select"
              >
                <option value="">All Roles</option>
                {uniqueRoles.map(role => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="supervisorFilter">Supervisor</label>
              <select
                id="supervisorFilter"
                value={localFilters.supervisor}
                onChange={(e) => handleFilterChange('supervisor', e.target.value)}
                className="filter-select"
              >
                <option value="">All Supervisors</option>
                <option value="no_supervisor">No Supervisor</option>
                {availableSupervisors.map(supervisor => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.name} ({supervisor.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="statusFilter">Status</label>
              <select
                id="statusFilter"
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="lastLoginFromFilter">Last Login From</label>
              <input
                id="lastLoginFromFilter"
                type="date"
                title={BUSINESS_DAY_HINT}
                value={localFilters.lastLoginFrom}
                onChange={(e) => handleFilterChange('lastLoginFrom', e.target.value)}
                className="filter-input date-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="lastLoginToFilter">Last Login To</label>
              <input
                id="lastLoginToFilter"
                type="date"
                title={BUSINESS_DAY_HINT}
                value={localFilters.lastLoginTo}
                onChange={(e) => handleFilterChange('lastLoginTo', e.target.value)}
                className="filter-input date-input"
              />
            </div>
          </div>

          {/* Filter Tips */}
          <div className="filter-tips">
            <small>
              💡 <strong>Tips:</strong> Use Global Search for quick filtering, or use specific field filters for precise results. 
              All text filters support partial matching (case-insensitive).
            </small>
          </div>
        </div>
      )}
    </div>
  );
};